import { useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView, Text, TextInput, TextInputChangeEvent, View } from "react-native";
import MaterialCommunityIcons from "react-native-vector-icons/MaterialCommunityIcons";
import { axiosInstance } from "../../api/Middleware";
import { useSessionStore } from "../../lib/Store/sessionStore.component";
import { CET, MouvementCET, SaisieDuree, TypeMouvement } from "./GestionCET.types";
import dayjs from "dayjs";
import { Quotas } from "../../api/Dotation/types";
import useGestionCETStyle, { couleursCET } from "./GestionCET.styles";
import useEnTetePage from "../../components/EnTetePage/EnTetePage";
import DotationEpargnable, { couleurFond, formatDisponible } from "./DotationEpargnable";
import Dropdown from "../../components/Dropdown/Dropdown";
import { DemandeDotationType, LigneCETType } from "../../api/CET/types";
import { useNotify } from "../../components/Notification";

const TAGS: Record<TypeMouvement, { libelle: string, bg: string, fg: string }> = {
    epargne: { libelle: "Épargne", bg: couleursCET.vertBg, fg: couleursCET.vertFg },
    prise: { libelle: "Prise", bg: couleursCET.grey100, fg: couleursCET.grey600 },
    paiement: { libelle: "Paiement", bg: '#E8EEF9', fg: couleursCET.bleu },
    rafp: { libelle: "R.A.F.P.", bg: couleursCET.metierTonique, fg: couleursCET.metierSecondaire },
    attente: { libelle: "En attente de validation", bg: couleursCET.orangeBg, fg: couleursCET.orangeFg },
}

const formatJours = (valeur?: number) => (valeur || 0).toFixed(2).replace('.', ',');
const toNumber = (valeur?: string | number | null) => Number(String(valeur ?? '').replace(',', '.')) || 0;
const formatHeures = (totalMinutes: number) => {
    const heures = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return minutes ? `${heures} h ${String(minutes).padStart(2, '0')}` : `${heures} h`;
}
// Les durées en heures des dotations sont au format H.MM (ex : 2.30 = 2 h 30)
const heuresEnMinutes = (valeur?: string | number | null) => {
    const duree = toNumber(valeur);
    const heures = Math.trunc(duree);
    return heures * 60 + Math.round((duree - heures) * 100);
}

// Catégorie du mouvement selon la nature d'utilisation de la ligne CET
const NATURES_UTILISATION: Record<number, { type: TypeMouvement, libelle: string }> = {
    0: { type: 'epargne', libelle: 'Épargne' },
    1: { type: 'prise', libelle: 'Congés pris sur le CET' },
    2: { type: 'paiement', libelle: 'Jours payés' },
    3: { type: 'rafp', libelle: 'Versement R.A.F.P.' },
}

// Transforme une ligne du compte CET en mouvement de l'historique
const toMouvement = (ligne: LigneCETType, natures: Quotas[]): MouvementCET => {
    const jours = Math.abs(toNumber(ligne.duree));
    const { type, libelle } = NATURES_UTILISATION[ligne.natureUtilisation ?? 0] ?? NATURES_UTILISATION[0];
    const base = { cle: `ligne-${ligne.identifiant}`, date: dayjs(ligne.date).format('DD/MM/YYYY'), horodatage: dayjs(ligne.date).valueOf(), jours, type };
    if(type === 'epargne'){
        const nature = natures.find((dotation) => dotation.identifiantNatureAbsence === ligne.identifiantNatureAbsence)?.natureAbsence;
        return {
            ...base,
            libelle: nature?.libelle || ligne.observation || libelle,
            couleur: nature?.couleurHexa,
            valeur: `+ ${formatJours(jours)} j`,
        };
    }
    const bulletin = ligne.moisBp && ligne.anneeBp ? ` (bulletin ${String(ligne.moisBp).padStart(2, '0')}/${ligne.anneeBp})` : '';
    return { ...base, libelle: `${libelle}${bulletin}`, valeur: `− ${formatJours(jours)} j` };
}

// Transforme une demande d'épargne en attente de validation en mouvement de l'historique
const toMouvementDemande = (demande: DemandeDotationType, heuresParJour: number): MouvementCET => {
    const minutes = heuresEnMinutes(demande.heure);
    const valeur = demande.duree?.texte
        || (demande.jour ? `${formatJours(demande.jour)} j` : formatHeures(minutes));
    return {
        cle: `demande-${demande.identifiant}`,
        date: dayjs(demande.dateDemande).format('DD/MM/YYYY'),
        horodatage: dayjs(demande.dateDemande).valueOf(),
        libelle: demande.natureAbsence?.libelle || "Demande d'épargne",
        type: 'attente',
        couleur: demande.natureAbsence?.couleurHexa,
        valeur: `+ ${valeur}`,
        jours: demande.duree?.dureeJours ?? (demande.jour || 0) + minutes / 60 / heuresParJour,
    };
}

export default function GestionCET() {
    const { getCurrentInfos } = useSessionStore();
    const { currentEntite, currentLogin } = getCurrentInfos();
    const { styles } = useGestionCETStyle();
    const { EnTete } = useEnTetePage({text: "Mon Compte Épargne Temps"})
    const notify = useNotify();
    const [soldes, setSoldes] = useState<CET>()
    const [dotationsEpargnables, setDotationsEpargnables] = useState<Quotas[]>([]);
    const [selectedDotation, setSelectedDotation] = useState<Quotas>();
    const [duree, setDuree] = useState<SaisieDuree>({jours: '', heures: '', minutes: ''});
    const [lignesCET, setLignesCET] = useState<LigneCETType[]>([]);
    const [demandesCET, setDemandesCET] = useState<DemandeDotationType[]>([]);
    const [historiqueOuvert, setHistoriqueOuvert] = useState(false);
    const heuresParJour = useMemo(() => currentEntite?.collectivitegr?.dureeJourEnHeureCet || 7, [currentEntite?.collectivitegr?.dureeJourEnHeureCet]);
    const plafondCet = useMemo(() => currentEntite?.collectivitegr?.limiteCet || 60, [currentEntite?.collectivitegr?.limiteCet]);

    const demandesEnAttente = useMemo(() => (
        demandesCET.map((demande) => toMouvementDemande(demande, heuresParJour))
    ), [demandesCET, heuresParJour]);

    // Demandes en attente et lignes du compte CET, du plus récent au plus ancien
    const mouvements = useMemo(() => [
        ...demandesEnAttente,
        ...lignesCET.map((ligne) => toMouvement(ligne, dotationsEpargnables)),
    ].sort((a, b) => b.horodatage - a.horodatage), [demandesEnAttente, dotationsEpargnables, lignesCET]);

    const unite = useMemo(() => {
        if(selectedDotation?.jourHeure === 2){
            return "heures"
        }
        return "jours"
    }, [selectedDotation])
    const couleurNature = selectedDotation?.natureAbsence?.couleurHexa || couleursCET.grey450;

    // Jours encore épargnables avant d'atteindre le plafond réglementaire
    const soldeReste = toNumber(soldes?.reste);
    const plafondRestant = Math.max(0, plafondCet - soldeReste);

    const saisie = useMemo(() => {
        if(!selectedDotation){
            return { valide: false, enErreur: false, message: '' };
        }
        let erreurFormat = '';
        let quantite: number;
        let maximum: number;
        let disponible: number;
        let libelleMaximum: string;
        let saisieEffectuee: boolean;
        if(unite === 'heures'){
            saisieEffectuee = duree.heures !== '' || duree.minutes !== '';
            if(Number(duree.minutes) > 59){
                erreurFormat = 'Seuls les nombres entiers entre 0 et 59 sont acceptés pour les minutes';
            }
            quantite = Number(duree.heures) * 60 + Number(duree.minutes);
            disponible = (selectedDotation.resteHeures || 0) * 60 + (selectedDotation.resteMinutes || 0);
            maximum = Math.min(disponible, Math.round(plafondRestant * heuresParJour * 60));
            libelleMaximum = formatHeures(maximum);
        } else {
            saisieEffectuee = duree.jours !== '';
            quantite = toNumber(duree.jours);
            if(!Number.isInteger(quantite * 2)){
                erreurFormat = 'Seuls les nombres entiers ou les demi-journées sont acceptés';
            }
            disponible = toNumber(selectedDotation.resteJours);
            maximum = Math.min(disponible, plafondRestant);
            libelleMaximum = `${formatJours(maximum)} jours`;
        }
        const depassement = quantite > maximum;
        const cause = maximum < disponible
            ? `le plafond réglementaire de ${plafondCet} jours (${formatJours(soldeReste)} jours au compte)`
            : `la dotation de ${formatDisponible(selectedDotation)}`;
        const enErreur = saisieEffectuee && (!!erreurFormat || depassement);
        return {
            valide: !erreurFormat && quantite > 0 && !depassement,
            enErreur,
            message: erreurFormat || `${depassement ? 'Valeur non autorisée · ' : ''}Maximum ${libelleMaximum}, limité par ${cause}.`,
        };
    }, [soldeReste, duree.heures, duree.jours, duree.minutes, heuresParJour, plafondCet, plafondRestant, selectedDotation, unite]);

    const handleSaisieJours = useCallback((e: TextInputChangeEvent) => {
        if(e.nativeEvent.text.match(/^[0-9]*([.,][0-9]*)?$/)){
            setDuree((old) => ({...old, jours: e.nativeEvent.text}));
        }
    }, [])

    const handleSaisieHeures = useCallback((e: TextInputChangeEvent) => {
        if(e.nativeEvent.text.match(/^[0-9]*$/)){
            setDuree((old) => ({...old, heures: e.nativeEvent.text}));
        }
    }, [])

    const handleSaisieMinutes = useCallback((e: TextInputChangeEvent) => {
        if(e.nativeEvent.text.match(/^[0-9]{0,2}$/)){
            setDuree((old) => ({...old, minutes: e.nativeEvent.text}));
        }
    }, [])

    const changerDotation = useCallback((quota?: Quotas) => {
        setSelectedDotation(quota);
        setDuree({jours: '', heures: '', minutes: ''});
    }, [])

    const handleSelectDotation = useCallback((quota: Quotas) => changerDotation(quota), [changerDotation])

    // Un clic sur la dotation déjà sélectionnée la désélectionne
    const handlePressDotation = useCallback((quota: Quotas) => {
        changerDotation(selectedDotation?.identifiant === quota.identifiant ? undefined : quota);
    }, [changerDotation, selectedDotation?.identifiant])

    const toggleHistorique = useCallback(() => setHistoriqueOuvert((old) => !old), [])

    const handleCreateDemande = useCallback(() => {
        if(!selectedDotation || !saisie.valide){
            return;
        }
        const jours = toNumber(duree.jours);
        const heures = Number(duree.heures);
        const minutes = Number(duree.minutes);
        const libelle = selectedDotation.natureAbsence?.libelle || '';
        const valeur = unite === 'heures' ? formatHeures(heures * 60 + minutes) : `${formatJours(jours)} j`;
        axiosInstance.post("/personnel/personnel/demandesdotations", {
            identifiantAgent: currentLogin?.identifiant,
            identifiantEntite: currentEntite?.identifiantEntite,
            identifiantNatureAbsence: selectedDotation.natureAbsence?.identifiant,
            identifiantDomaine: currentLogin?.identifiantDomaine,
            unite,
            jours,
            heures,
            minutes
        }).then((response) => {
            setDotationsEpargnables((old) => old.map((dotation) => {
                if(dotation.identifiantNatureAbsence !== response.data.identifiantNatureAbsence){
                    return dotation;
                }
                if(dotation.jourHeure === 1){
                    return {...dotation, resteJours: String(toNumber(dotation.resteJours) - jours).replace('.', ',')};
                }
                let resteHeures = (dotation.resteHeures || 0) - heures;
                let resteMinutes = (dotation.resteMinutes || 0) - minutes;
                if(resteMinutes < 0){
                    resteMinutes += 60;
                    resteHeures -= 1;
                }
                return {...dotation, resteHeures, resteMinutes};
            }))
            // La demande créée n'embarque pas forcément sa nature d'absence : on reprend celle de la dotation sélectionnée
            const demande: DemandeDotationType = { ...response.data, natureAbsence: response.data.natureAbsence ?? selectedDotation.natureAbsence };
            // Demande validée automatiquement : le compte CET est crédité immédiatement
            const valideeAutomatiquement = demande.statut === 1 && demande.valide === 1;
            if(valideeAutomatiquement){
                const joursEpargnes = unite === 'heures' ? (heures * 60 + minutes) / 60 / heuresParJour : jours;
                setSoldes((old) => old && {
                    ...old,
                    reste: formatJours(toNumber(old.reste) + joursEpargnes),
                    epargnes: formatJours(toNumber(old.epargnes) + joursEpargnes),
                });
                axiosInstance.get(`/personnel/personnel/lignescets/agents/${currentLogin?.identifiant}`)
                    .then((res) => setLignesCET(Array.isArray(res.data) ? res.data : res.data?.data ?? []))
            } else {
                setDemandesCET((old) => [demande, ...old]);
            }
            setSelectedDotation(undefined);
            setDuree({jours: '', heures: '', minutes: ''});
            setHistoriqueOuvert(true);
            notify.success(valideeAutomatiquement
                ? `Épargne de ${valeur} (${libelle}) ajoutée à votre compte épargne temps.`
                : `Demande d'épargne de ${valeur} (${libelle}) transmise à votre valideur.`);
        }).catch(() => {
            notify.error("La demande n'a pas pu être envoyée. Veuillez réessayer.");
        })
    }, [currentEntite?.identifiantEntite, currentLogin?.identifiant, currentLogin?.identifiantDomaine, duree, heuresParJour, notify, saisie.valide, selectedDotation, unite])

    const getTextNature = useCallback((quota: Quotas) => (
        <View style={styles.modalItem}>
            <View style={[styles.pastille, { backgroundColor: quota.natureAbsence?.couleurHexa || couleursCET.grey450 }]}/>
            <Text style={styles.modalItemText}>{quota.natureAbsence?.libelle}</Text>
        </View>
    ), [styles.modalItem, styles.modalItemText, styles.pastille])

    useEffect(() => {
        axiosInstance.get(`/personnel/personnel/cets/dotation-agent?identifiantEntite=${currentEntite?.identifiantEntite}&identifiantAgent=${currentLogin?.identifiant}`)
            .then((response) => setSoldes(response.data))
        axiosInstance.get(`/personnel/personnel/naturesabsences/epargnables-cet?identifiantEntite=${currentEntite?.identifiantEntite}&identifiantAgent=${currentLogin?.identifiant}`)
            .then((response) => setDotationsEpargnables(response.data))
        axiosInstance.get(`/personnel/personnel/lignescets/agents/${currentLogin?.identifiant}`)
            .then((response) => setLignesCET(Array.isArray(response.data) ? response.data : response.data?.data ?? []))
        axiosInstance.get(`/personnel/personnel/demandesdotations?identifiantAgent=${currentLogin?.identifiant}&valide=0&statut=0&withNatureAbsence=1`)
            .then((response) => setDemandesCET(Array.isArray(response.data) ? response.data : response.data?.data ?? []))
    }, [currentEntite?.identifiantEntite, currentLogin?.identifiant])

    const bordureSaisie = useMemo(() => saisie.enErreur ? couleursCET.danger : undefined , [saisie.enErreur]);

    return(
        <ScrollView contentContainerStyle={styles.container}>
            {EnTete}
            <View style={styles.etatCard}>
                <View style={styles.etatHeader}>
                    <Text style={styles.title}>État du compte au {dayjs().format('DD/MM/YYYY')}</Text>
                    <View style={styles.badgePlafond}>
                        <Text style={styles.badgePlafondText}>Plafond réglementaire {plafondCet} jours</Text>
                    </View>
                </View>
                <View style={styles.mainInfo}>
                    <Text style={styles.primaryLabel}>Il vous reste</Text>
                    <Text style={styles.primaryValue}>{formatJours(toNumber(soldes?.reste))} jours</Text>
                </View>

                <View style={styles.quickStats}>
                    {[
                        { libelle: 'Épargnés', valeur: soldes?.epargnes },
                        { libelle: 'Pris', valeur: soldes?.pris },
                        { libelle: 'Payés', valeur: soldes?.payes },
                        { libelle: 'R.A.F.P.', valeur: soldes?.rafp },
                    ].map((stat) => (
                        <View key={stat.libelle} style={styles.statItem}>
                            <Text style={styles.statLabel}>{stat.libelle}</Text>
                            <Text style={styles.statValue}>{stat.valeur}</Text>
                        </View>
                    ))}
                </View>

                <View style={styles.histoContainer}>
                    <Pressable onPress={toggleHistorique} style={styles.histoBouton}>
                        <MaterialCommunityIcons name="history" size={17} color={couleursCET.grey600}/>
                        <Text style={styles.histoTitre}>Historique des mouvements</Text>
                        <Text style={styles.histoNb}>{mouvements.length} mouvement{mouvements.length > 1 ? 's' : ''}</Text>
                        <MaterialCommunityIcons name={historiqueOuvert ? "chevron-up" : "chevron-down"} size={18} color={couleursCET.grey600}/>
                    </Pressable>
                    {historiqueOuvert && (
                        <View style={styles.histoListe}>
                            {mouvements.length === 0 && (
                                <Text style={styles.histoVide}>Aucun mouvement</Text>
                            )}
                            {mouvements.map((mouvement) => {
                                const tag = TAGS[mouvement.type];
                                const couleurValeur = mouvement.type === 'attente'
                                    ? couleursCET.orangeFg
                                    : mouvement.type === 'epargne' ? couleursCET.succes : couleursCET.grey800;
                                return (
                                    <View key={mouvement.cle} style={styles.histoLigne}>
                                        <Text style={styles.histoDate}>{mouvement.date}</Text>
                                        <View style={styles.histoLibelleContainer}>
                                            <View style={[styles.pastille, { backgroundColor: mouvement.couleur || couleursCET.grey450 }]}/>
                                            <Text style={styles.histoLibelle}>{mouvement.libelle}</Text>
                                            <View style={[styles.tag, { backgroundColor: tag.bg }]}>
                                                <Text style={[styles.tagText, { color: tag.fg }]}>{tag.libelle}</Text>
                                            </View>
                                        </View>
                                        <Text style={[styles.histoValeur, { color: couleurValeur }]}>{mouvement.valeur}</Text>
                                    </View>
                                )
                            })}
                        </View>
                    )}
                </View>
            </View>

            <View style={styles.subContent}>
                <View style={styles.column}>
                    <Text style={styles.sectionTitre}>Dotations épargnables</Text>
                    <View style={styles.dotationsContainer}>
                        {dotationsEpargnables.map((dotation) => (
                            <DotationEpargnable
                                key={dotation.identifiant}
                                dotation={dotation}
                                selected={selectedDotation?.identifiant === dotation.identifiant}
                                dimmed={!!selectedDotation && selectedDotation.identifiant !== dotation.identifiant}
                                onPress={handlePressDotation}
                            />
                        ))}
                    </View>
                    <View style={styles.piedDotations}>
                        <Text style={styles.aide}>Sélectionnez une dotation pour la reprendre dans votre demande.</Text>
                        <View style={styles.conversion}>
                            <MaterialCommunityIcons name="information-outline" size={14} color={couleursCET.grey600}/>
                            <Text style={styles.conversionText}>
                                Conversion : 1 journée = {heuresParJour} heures
                            </Text>
                        </View>
                    </View>
                </View>
                <View style={styles.column}>
                    <Text style={styles.sectionTitre}>Demande d'épargne CET</Text>
                    <View style={styles.formCard}>
                        <View style={styles.champ}>
                            <Text style={styles.champLabel}>Nature d'absence <Text style={styles.obligatoire}>*</Text></Text>
                            <View style={styles.dropdownWrapper}>
                                <View style={[styles.dropdownPastille, { backgroundColor: couleurNature }]} pointerEvents="none"/>
                                <Dropdown
                                    value={selectedDotation?.natureAbsence?.libelle}
                                    options={dotationsEpargnables}
                                    emptyText="Choisir une nature d'absence"
                                    getText={getTextNature}
                                    onSelectOption={handleSelectDotation}
                                    style={styles.dropdown}
                                />
                            </View>
                        </View>

                        {selectedDotation && (
                            <>
                                <View style={[styles.bandeauNature, { backgroundColor: couleurFond(selectedDotation.natureAbsence?.couleurHexa) }]}>
                                    <MaterialCommunityIcons name="link-variant" size={16} color={couleurNature}/>
                                    <Text style={styles.bandeauNatureText}>
                                        {selectedDotation.natureAbsence?.libelle} · épargnable {formatDisponible(selectedDotation)} · plafond restant {formatJours(plafondRestant)} j
                                    </Text>
                                </View>

                                <View style={styles.champ}>
                                    <Text style={styles.champLabel}>
                                        {unite === 'heures' ? "Nombre d'heures à épargner" : "Nombre de jours à épargner"} <Text style={styles.obligatoire}>*</Text>
                                    </Text>
                                    {unite === "heures" ? (
                                        <View style={styles.saisieLigne}>
                                            <TextInput
                                                value={duree.heures}
                                                onChange={handleSaisieHeures}
                                                keyboardType="number-pad"
                                                placeholder="0"
                                                style={[styles.formInput, { borderColor: bordureSaisie }]}
                                            />
                                            <Text style={styles.unite}>h</Text>
                                            <TextInput
                                                value={duree.minutes}
                                                onChange={handleSaisieMinutes}
                                                keyboardType="number-pad"
                                                placeholder="0"
                                                style={[styles.formInput, { borderColor: bordureSaisie }]}
                                            />
                                            <Text style={styles.unite}>min</Text>
                                        </View>
                                    ) : (
                                        <View style={styles.saisieLigne}>
                                            <TextInput
                                                value={duree.jours}
                                                onChange={handleSaisieJours}
                                                keyboardType="decimal-pad"
                                                placeholder="0"
                                                style={[styles.formInput, { borderColor: bordureSaisie }]}
                                            />
                                            <Text style={styles.unite}>jours</Text>
                                        </View>
                                    )}
                                    <Text style={[styles.aide, saisie.enErreur && styles.aideErreur]}>{saisie.message}</Text>
                                </View>
                            </>
                        )}

                        <Pressable
                            disabled={!saisie.valide}
                            onPress={handleCreateDemande}
                            style={[styles.buttonAccept, !saisie.valide && styles.buttonAcceptDisabled]}
                        >
                            <Text style={[styles.buttonText, !saisie.valide && styles.buttonTextDisabled]}>Valider</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </ScrollView>
    )
}
